import DeleteIcon from "@mui/icons-material/DeleteOutline";
import {
  Alert,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import * as React from "react";

import { MetricDefinitionRecord } from "@/shared/domain/metrics";
import {
  useAllMetricDefinitions,
  useDeleteMetricDefinition,
  useUpdateMetricDefinition,
} from "@/shared/domain/metrics/hooks";
import { Button } from "@/shared/foundation/primitives";

import styles from "./MetricDefinitionsTable.module.css";

export interface MetricDefinitionsTableProps {
  orgId: string | undefined;
}

/**
 * Management table for an org's metric definitions: lists all definitions and
 * supports editing a definition's description (row click) or deleting it.
 * Deletes are gated behind a confirmation dialog because they are org-wide and
 * irreversible. The list is org-wide — definitions are not device/session
 * scoped.
 */
export function MetricDefinitionsTable({ orgId }: MetricDefinitionsTableProps) {
  const { data, isLoading, error } = useAllMetricDefinitions(orgId);
  const [editing, setEditing] = React.useState<MetricDefinitionRecord | null>(
    null,
  );
  const [deleting, setDeleting] = React.useState<MetricDefinitionRecord | null>(
    null,
  );

  return (
    <div className={styles.card}>
      {isLoading && (
        <div className={styles.state}>
          <CircularProgress size={24} />
        </div>
      )}
      {error && (
        <Alert severity="error">
          Failed to load definitions: {error.message}
        </Alert>
      )}
      {!isLoading && !error && (data?.length ?? 0) === 0 && (
        <p className={styles.empty}>No metric definitions yet.</p>
      )}
      {!isLoading && !error && (data?.length ?? 0) > 0 && (
        <TableContainer>
          <Table size="small" aria-label="metric definitions">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Modified</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(data ?? []).map((def) => (
                <TableRow
                  key={def.metric_id}
                  hover
                  className={styles.rowClickable}
                  onClick={() => setEditing(def)}
                >
                  <TableCell>{def.name}</TableCell>
                  <TableCell>{def.description ?? "—"}</TableCell>
                  <TableCell>
                    {new Date(def.created).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    {new Date(def.modified).toLocaleString()}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Delete definition">
                      <span>
                        <IconButton
                          size="small"
                          aria-label={`delete ${def.name}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleting(def);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {editing && (
        <EditDefinitionDialog
          definition={editing}
          orgId={orgId}
          onClose={() => setEditing(null)}
        />
      )}

      {deleting && (
        <DeleteDefinitionDialog
          definition={deleting}
          orgId={orgId}
          onClose={() => setDeleting(null)}
        />
      )}
    </div>
  );
}

function DeleteDefinitionDialog({
  definition,
  orgId,
  onClose,
}: {
  definition: MetricDefinitionRecord;
  orgId: string | undefined;
  onClose: () => void;
}) {
  const deleteMutation = useDeleteMetricDefinition();

  const handleDelete = () => {
    deleteMutation.mutate(
      { name: definition.name, orgId },
      { onSuccess: onClose },
    );
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Delete metric definition</DialogTitle>
      <DialogContent dividers>
        <DialogContentText>
          This permanently deletes <strong>{definition.name}</strong> and all
          its historical data for the whole organization. This action cannot be
          undone.
        </DialogContentText>
        {deleteMutation.error && (
          <Alert severity="error" className={styles.alert}>
            Failed to delete definition: {deleteMutation.error.message}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          loading={deleteMutation.isPending}
          onClick={handleDelete}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function EditDefinitionDialog({
  definition,
  orgId,
  onClose,
}: {
  definition: MetricDefinitionRecord;
  orgId: string | undefined;
  onClose: () => void;
}) {
  const [description, setDescription] = React.useState(
    definition.description ?? "",
  );
  const updateMutation = useUpdateMetricDefinition();

  const handleSave = () => {
    updateMutation.mutate(
      { name: definition.name, request: { description }, orgId },
      { onSuccess: onClose },
    );
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit {definition.name}</DialogTitle>
      <DialogContent dividers>
        <TextField
          className={styles.field}
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          multiline
          minRows={2}
        />
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          loading={updateMutation.isPending}
          onClick={handleSave}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
